import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getPaymentStatus, capturePayPal } from "@/api";
import Seo from "@/components/Seo";

export default function PaymentSuccess() {
  const { t, cart, removeFromCart } = useApp();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const paypalToken = params.get("token"); // PayPal appends ?token=ORDER_ID
  const [state, setState] = useState("processing"); // processing | paid | failed
  const [orderNumber, setOrderNumber] = useState(null);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const cleared = useRef(false);
  const started = useRef(false);

  const clearCart = () => {
    if (!cleared.current) { cleared.current = true; cart.forEach((p) => removeFromCart(p.key)); }
  };

  const markPaid = (orderNo) => {
    setOrderNumber(orderNo || null);
    setState("paid");
    setThankYouOpen(true);
    clearCart();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (started.current) return;
    started.current = true;

    // PayPal return flow — capture the approved order
    if (provider === "paypal" && paypalToken) {
      capturePayPal(paypalToken)
        .then((data) => {
          if (data.payment_status === "paid") { markPaid(data.order_number); }
          else setState("failed");
        })
        .catch(() => setState("failed"));
      return;
    }

    // Stripe flow — poll status
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") { markPaid(data.order_number); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      attempts += 1;
      if (attempts >= 12) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-[70vh] pt-32 md:pt-40 pb-24 px-5 md:px-10 flex flex-col items-center justify-center text-center" data-testid="payment-success-page">
      <Seo title={t.seo.success.title} description={t.seo.success.description} noindex />
      {state === "processing" && (
        <div data-testid="payment-processing">
          <Loader2 className="h-10 w-10 animate-spin mx-auto mb-6 text-accent" />
          <p className="font-display text-3xl">{t.payment.processing}</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid">
          <div className="h-16 w-16 rounded-full bg-ink text-paper flex items-center justify-center mx-auto mb-6"><Check className="h-7 w-7" /></div>
          <h1 className="font-display text-5xl md:text-6xl mb-4">{t.payment.successTitle}</h1>
          {orderNumber && (
            <div className="inline-block border border-line px-6 py-3 mb-6" data-testid="success-order-number">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">{t.payment.orderNumber}</p>
              <p className="font-display text-3xl">#{orderNumber}</p>
            </div>
          )}
          <p className="text-ink/60 font-light max-w-md mx-auto mb-8">{t.payment.successMsg}</p>
          <Link to="/catalogue" className="inline-block bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink" data-testid="success-continue">{t.payment.continue}</Link>
        </div>
      )}
      {state === "failed" && (
        <div data-testid="payment-failed">
          <div className="h-16 w-16 rounded-full border border-line flex items-center justify-center mx-auto mb-6"><X className="h-7 w-7" /></div>
          <h1 className="font-display text-5xl md:text-6xl mb-4">{t.payment.failedTitle}</h1>
          <p className="text-ink/60 font-light max-w-md mx-auto mb-8">{t.payment.failedMsg}</p>
          <Link to="/payment" className="inline-block bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink" data-testid="failed-retry">{t.payment.payButton}</Link>
        </div>
      )}

      {/* Personalized thank-you modal shown once the payment is confirmed */}
      <AnimatePresence>
        {thankYouOpen && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-5"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="thank-you-modal"
          >
            <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={() => setThankYouOpen(false)} />
            <motion.div
              className="relative bg-paper text-ink max-w-md w-full px-8 py-10 md:px-10 md:py-12 text-center shadow-[0_24px_80px_-20px_rgba(0,0,0,0.5)]"
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                onClick={() => setThankYouOpen(false)}
                className="absolute top-4 right-4 text-ink/40 hover:text-ink transition-colors"
                aria-label="Close"
                data-testid="thank-you-close"
              >
                <X className="h-5 w-5" strokeWidth={1.4} />
              </button>
              <div className="h-14 w-14 rounded-full bg-ink text-paper flex items-center justify-center mx-auto mb-6">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="font-display text-4xl md:text-5xl mb-4" data-testid="thank-you-title">{t.payment.thankYouTitle}</h2>
              <p className="text-ink/70 font-light leading-relaxed mb-8" data-testid="thank-you-message">
                {t.payment.thankYouLead} {orderNumber ? `#${orderNumber} ` : ""}{t.payment.thankYouTrail}
              </p>
              <Link
                to="/"
                onClick={() => setThankYouOpen(false)}
                className="inline-block w-full bg-ink text-paper px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink"
                data-testid="thank-you-continue"
              >
                {t.payment.continueShopping}
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
